import React, { useCallback, useMemo, useState } from 'react';
import { arrayOf, func, shape, number, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Table } from 'antd';
import { Card } from '@cards/Card';
import { pathwayRuleType } from '../../../../../propTypes';
import SelectRulesModal from '../StagedRules/SelectRulesModal';
import SelectCTAModal from './SelectCTAModal';
import createColumns from './createColumns';
import '@pathways/cards/Pathways/CareTeamPermissions/styles.less';

function AddRulesToRole({
  title,
  selectedPermissions,
  addPermissions,
  removePermission,
  updatePermissionType,
  customRules,
  allAvailableRules,
  callToActionList,
}) {
  const [isSelectRuleVisible, setIsSelectRuleVisible] = useState(false);
  const [isSelectCTAVisible, setIsSelectCTAVisible] = useState(false);
  const { t } = useTranslation();

  const columns = useMemo(() => createColumns(t, updatePermissionType, allAvailableRules), [
    t,
    updatePermissionType,
    allAvailableRules,
  ]);

  const dataSource = useMemo(
    () =>
      selectedPermissions.map(permission => ({
        ...permission,
        onDelete: () => removePermission(permission.id || permission.rule?.id),
      })),
    [selectedPermissions, removePermission],
  );

  const toggleSelectRule = useCallback(() => setIsSelectRuleVisible(!isSelectRuleVisible), [
    isSelectRuleVisible,
  ]);

  const toggleSelectCTA = useCallback(() => setIsSelectCTAVisible(!isSelectCTAVisible), [
    isSelectCTAVisible,
  ]);

  const handleAddRules = useCallback(
    newRules => {
      const newPermissions = newRules.map(rule => ({
        rule,
        permissionType: 'send_notification',
        type: 'rule',
      }));
      addPermissions(newPermissions);
    },
    [addPermissions],
  );

  const handleAddCTAs = useCallback(
    newCTAs => {
      const newPermissions = newCTAs.map(cta => ({
        id: cta.id,
        contentRef: cta.value,
        callToAction: cta,
        permissionType: 'send_notification',
        type: 'cta',
      }));
      addPermissions(newPermissions);
    },
    [addPermissions],
  );

  const selectedRules = useMemo(
    () => selectedPermissions.filter(p => p.type === 'rule' || p.rule).map(p => p.rule),
    [selectedPermissions],
  );

  const selectedCTAIds = useMemo(() => {
    const ids = new Set();
    selectedPermissions.forEach(p => {
      if (p.type === 'cta' || p.callToAction) {
        ids.add(p.id || p.callToAction?.id);
      }
    });
    return ids;
  }, [selectedPermissions]);

  return (
    <>
      <Card.Full
        className="role-rules-card"
        extra={
          <>
            <Button icon="plus" type="dashed" onClick={toggleSelectRule}>
              {t('pathways:ProcedurePathways.wizard.careTeamRules.addRule')}
            </Button>
            <Button icon="plus" type="dashed" onClick={toggleSelectCTA} style={{ marginLeft: 8 }}>
              {t('pathways:ProcedurePathways.wizard.careTeamRules.addCallToAction')}
            </Button>
          </>
        }
        title={title}
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          rowKey={record => record.id || record.rule?.id}
        />
      </Card.Full>
      {isSelectRuleVisible ? (
        <SelectRulesModal
          customRules={customRules}
          filters={[]}
          selectedRules={selectedRules}
          singleSelect={false}
          onCancel={toggleSelectRule}
          onOk={handleAddRules}
        />
      ) : null}
      {isSelectCTAVisible ? (
        <SelectCTAModal
          callToActionList={callToActionList}
          selectedCTAIds={selectedCTAIds}
          onCancel={toggleSelectCTA}
          onOk={handleAddCTAs}
        />
      ) : null}
    </>
  );
}

AddRulesToRole.propTypes = {
  selectedPermissions: arrayOf(pathwayRuleType),
  title: string.isRequired,
  addPermissions: func.isRequired,
  removePermission: func.isRequired,
  updatePermissionType: func.isRequired,
  customRules: arrayOf(pathwayRuleType),
  allAvailableRules: arrayOf(pathwayRuleType),
  callToActionList: arrayOf(
    shape({
      id: number.isRequired,
      name: string.isRequired,
      type: string.isRequired,
      value: string.isRequired,
      audience: string.isRequired,
    }),
  ),
};

AddRulesToRole.defaultProps = {
  selectedPermissions: [],
  customRules: [],
  allAvailableRules: [],
  callToActionList: [],
};

export default AddRulesToRole;
